import React, { useState, useEffect } from "react";
import { 
  Download, Filter, ChevronLeft, ChevronRight, 
  AlertTriangle, Calendar, Clock, Loader2, Search 
} from "lucide-react";

// CONFIGURATION
const BASE_URL = "https://api-dg3-core-inv5.adminpaymentdailygo.co.in"; 
const API_URL = `${BASE_URL}/api/bills`;

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter States
  const [searchText, setSearchText] = useState(""); // Changed from selectedFOS to searchText
  const [selectedDate, setSelectedDate] = useState(""); 
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;

  // --- 1. Fetch Data from Backend ---
  useEffect(() => {
    const fetchBills = async () => {
      try {
        const response = await fetch(API_URL);
        if (!response.ok) {
          throw new Error("Failed to fetch bills");
        }
        const data = await response.json();

        // Transform MongoDB data to UI format
        const formattedData = data.map((bill, index) => {
            const dateObj = new Date(bill.createdAt);
            
            return {
                id: bill._id, 
                // Ensure name is a string to prevent errors during search
                name: bill.salesExecutive || "Unknown",
                email: bill.email || "No Email Provided",
                invoiceId: bill.invoiceNo,
                pdfPath: bill.pdfPath, 
                
                // Formatted for UI display
                dateDisplay: dateObj.toLocaleDateString('en-US', { 
                    month: 'short', day: '2-digit', year: 'numeric' 
                }),
                // Formatted for Filtering (YYYY-MM-DD)
                rawDate: dateObj.toISOString().split('T')[0],
                
                time: dateObj.toLocaleTimeString('en-US', { 
                    hour: '2-digit', minute: '2-digit' 
                }),
                initials: bill.salesExecutive ? bill.salesExecutive.split(' ').map(n => n[0]).join('').substring(0,2).toUpperCase() : "NA",
                color: getColor(index)
            };
        });

        setBills(formattedData);
      } catch (err) {
        console.error("Error fetching bills:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBills();
  }, []);

  // --- 2. Handle File Download ---
  const handleDownload = async (pdfPath, invoiceId) => {
    if (!pdfPath) {
        return alert("PDF was not generated for this bill.");
    }
    try {
        const filename = pdfPath.split(/[/\\]/).pop();
        const fileUrl = `${BASE_URL}/uploads/${filename}`;
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error("File not found on server");
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Invoice_${invoiceId}.pdf`; 
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (err) {
        console.error("Download error:", err);
        alert("Failed to download file. It might have been deleted.");
    }
  };

  // --- Helper: Color Palette ---
  const getColor = (index) => {
    const colors = [
        "from-indigo-500 to-blue-600",
        "from-pink-500 to-rose-600",
        "from-emerald-500 to-teal-600",
        "from-orange-500 to-amber-600",
        "from-cyan-500 to-blue-600",
        "from-violet-500 to-purple-600"
    ];
    return colors[index % colors.length];
  };

  // --- Filter Logic (Search Text + Date) ---
  const filteredData = bills.filter(bill => {
    // 1. Check Search Match (Name or potentially ID if included in name string)
    // Uses includes() for partial matching and toLowerCase() for case insensitivity
    const searchMatch = searchText === "" || 
                        bill.name.toLowerCase().includes(searchText.toLowerCase()) ||
                        bill.invoiceId.toLowerCase().includes(searchText.toLowerCase()); // Added Invoice ID search too

    // 2. Check Date Match
    const dateMatch = !selectedDate || bill.rawDate === selectedDate;

    return searchMatch && dateMatch;
  });

  // --- Pagination Logic ---
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentBills = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedDate]);

  if (isLoading) {
    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mb-2" size={32} />
            <p>Loading Invoices...</p>
        </div>
    );
  }

  if (error) {
    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-rose-400">
            <AlertTriangle className="mb-2" size={32} />
            <p>Error: {error}</p>
        </div>
    );
  }

  return (
    <div className="p-6 md:p-10 pb-24 space-y-6 bg-slate-950 min-h-screen h-full w-full text-slate-200 font-sans overflow-y-auto">
      
      {/* --- Page Header --- */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Generated Bills</h1>
          <p className="text-slate-400 mt-1 text-sm">Manage and download invoices.</p>
        </div>
        <div className="flex items-center gap-3">
            <a href="/generate-bill" className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-indigo-500/20">
              Create New Bill
            </a>
        </div>
      </div>

      {/* --- Warning Banner --- */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-4 shadow-[0_0_15px_rgba(245,158,11,0.1)]">
        <div className="p-2 bg-amber-500/20 rounded-lg text-amber-400 shrink-0">
          <AlertTriangle size={20} />
        </div>
        <div>
          <h3 className="text-amber-200 font-semibold text-sm">Auto-Deletion Warning</h3>
          <p className="text-amber-200/70 text-xs mt-1 leading-relaxed">
            Old bills will be automatically deleted <span className="text-amber-100 font-bold">35 days</span> from their generation date.
          </p>
        </div>
      </div>

      {/* --- Main Content Card --- */}
      <div className="w-full bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-2xl overflow-hidden shadow-xl flex flex-col">
        
        {/* --- Toolbar: Filters --- */}
        <div className="p-5 border-b border-slate-800 flex flex-col lg:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 text-white font-bold text-lg">
             <Filter size={20} className="text-indigo-400" />
             <h2>Filter Invoices</h2>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            
            {/* Search Input (Replaced Dropdown) */}
            <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 z-10" size={16} />
                <input 
                    type="text"
                    placeholder="Search Name or Invoice ID..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full bg-slate-950/50 border border-slate-700 text-slate-300 text-sm rounded-lg pl-9 pr-4 py-2.5 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-600"
                />
            </div>

            {/* Date Picker */}
            <div className="relative w-full sm:w-48">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 z-10" size={16} />
                <input 
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full bg-slate-950/50 border border-slate-700 text-slate-300 text-sm rounded-lg pl-9 pr-4 py-2 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all cursor-pointer [color-scheme:dark]"
                />
            </div>

            {/* Clear Filters Button */}
            {(searchText || selectedDate) && (
                <button 
                    onClick={() => { setSearchText(""); setSelectedDate(""); }}
                    className="px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors border border-dashed border-slate-700 hover:border-slate-500"
                >
                    Reset
                </button>
            )}
          </div>
        </div>

        {/* --- Table Body --- */}
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/50 text-slate-400 text-xs uppercase border-b border-slate-800">
                <th className="p-4 font-medium">Generated By</th>
                <th className="p-4 font-medium">Date</th>
                <th className="p-4 font-medium">Time</th>
                <th className="p-4 font-medium">Invoice ID</th>
                <th className="p-4 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-800/50">
              {currentBills.length > 0 ? (
                currentBills.map((bill) => (
                  <tr key={bill.id} className="group hover:bg-slate-800/30 transition-colors">
                    {/* Name */}
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white bg-gradient-to-br ${bill.color}`}>
                          {bill.initials}
                        </div>
                        <div>
                          <div className="font-medium text-slate-200">{bill.name}</div>
                          <div className="text-xs text-slate-500">{bill.email}</div>
                        </div>
                      </div>
                    </td>

                    {/* Date */}
                    <td className="p-4 text-slate-400">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-slate-600" />
                        {bill.dateDisplay}
                      </div>
                    </td>

                    {/* Time */}
                    <td className="p-4 text-slate-400">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-slate-600" />
                        {bill.time}
                      </div>
                    </td>

                    {/* ID */}
                    <td className="p-4">
                      <span className="bg-slate-800 text-slate-400 text-xs px-2 py-1 rounded border border-slate-700 font-mono">
                        {bill.invoiceId}
                      </span>
                    </td>

                    {/* Download Button */}
                    <td className="p-4 text-right">
                      {bill.pdfPath ? (
                          <button 
                            onClick={() => handleDownload(bill.pdfPath, bill.invoiceId)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-lg text-xs font-medium transition-all group/btn"
                          >
                            <Download size={14} className="group-hover/btn:scale-110 transition-transform" />
                            Download
                          </button>
                      ) : (
                          <span className="text-xs text-slate-600 italic">No PDF</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="p-12 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                        <Search size={32} className="text-slate-700" />
                        <p>No bills found matching these filters.</p>
                        <button 
                            onClick={() => { setSearchText(""); setSelectedDate(""); }}
                            className="text-indigo-400 text-xs hover:underline mt-1"
                        >
                            Clear Filters
                        </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* --- Pagination Footer --- */}
        <div className="p-4 border-t border-slate-800 bg-slate-900/30 flex flex-col md:flex-row justify-between items-center gap-4 text-xs">
          <span className="text-slate-500">
            Showing <span className="text-white font-medium">{filteredData.length > 0 ? startIndex + 1 : 0}</span> to <span className="text-white font-medium">{Math.min(startIndex + itemsPerPage, filteredData.length)}</span> of <span className="text-white font-medium">{filteredData.length}</span> entries
          </span>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>

            {totalPages > 0 && Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                onClick={() => setCurrentPage(num)}
                className={`w-8 h-8 rounded-lg border text-xs font-medium transition-all ${
                  currentPage === num 
                    ? "bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-500/20" 
                    : "border-slate-700 bg-slate-800/50 text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
              >
                {num}
              </button>
            ))}

            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-2 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}